import { FieldInput } from 'sr-types/lib/person/v1/graphql';
import { FieldDefinition, Reference, Field as SearchField } from 'sr-types/lib/search/v1/graphql';
import { Attribute, AvailableTypeEnum, Field, FieldDefinitionWithDescription } from './attributes.types';
import { getSupportedItems } from './attributes.utils';

export const combineFieldDefinitionReferencesWithFieldInput = (
    associatedFieldDefinitionReferences: Reference[],
    fieldInputs: FieldInput[]
) => {
    const raw = associatedFieldDefinitionReferences?.map((fd) => {
        const attributes = fieldInputs?.find((attr) => attr.fieldDefinitionReference.id == fd.id);
        return {
            ...attributes,
            fieldDefinitionReference: fd
        };
    });
    return raw || [];
};

export const deriveEmptyAttributesFromFieldDefinitions = (fieldDefinitions: FieldDefinition[]) => {
    let attributes: Attribute[] = [];
    fieldDefinitions.forEach(({ __typename, about, fieldName, fieldType, group, id, label, metadata, reference }) => {
        attributes.push({
            id,
            typeName: __typename,
            fieldType: AvailableTypeEnum[fieldType],
            description: about,
            name: fieldName,
            label,
            group,
            metadata,
            fieldDefinitionReference: reference,
            value: []
        });
    });
    return attributes;
};

export const getAttributesByFieldDefinitions = (fieldDefinitionData, rawAttributes) => {
    const attributes = [];
    //In order to get more information about attributes like label, group, metadata, etc... we are mapping them with field definitions by id
    if (fieldDefinitionData) {
        fieldDefinitionData.forEach((field: FieldDefinition) => {
            const result = Array.isArray(rawAttributes)
                ? rawAttributes.find((attribute) => attribute.fieldDefinitionReference.id === field.id)
                : null;
            result && attributes.push({ ...field, ...result });
        });
    }

    return getSupportedItems(attributes);
};

const mapAttributesToFields = <
    T extends { fieldDefinitionReference?: Reference; fieldType?: string; name?: string; value?: any }
>(
    attributes: Attribute[],
    fieldFactory: (attr: Attribute) => T
): T[] => {
    const fieldsMap = new Map<string, T>();
    attributes?.forEach((attr) => {
        const refId = attr.fieldDefinitionReference?.id ?? attr.id;
        if (fieldsMap.has(refId)) {
            fieldsMap.get(refId)!.value = attr.value;
        } else {
            fieldsMap.set(refId, fieldFactory(attr));
        }
    });
    return Array.from(fieldsMap.values());
};

export const transformAttributesToFieldInputs = (attributes: Attribute[]): FieldInput[] => {
    return mapAttributesToFields<FieldInput>(attributes, (attr) => ({
        fieldDefinitionReference: attr.fieldDefinitionReference,
        fieldType: attr.fieldType,
        name: attr.name,
        value: attr.value
    }));
};

export const transformAttributesToField = (attributes: Attribute[]): SearchField[] => {
    return mapAttributesToFields<SearchField>(attributes, (attr) => ({
        fieldDefinitionReference: attr.fieldDefinitionReference,
        fieldType: attr.fieldType,
        name: attr.name,
        value: attr.value
    }));
};

export const extractFieldDefinitionReferencesFromAttributes = (attributes: Attribute[]) => {
    const references = attributes.map((a) => a.fieldDefinitionReference);
    return references;
};

export const getFieldDefinitionFromField = (
    field: Field & { metadata?: string; isRequired?: boolean; isKey?: boolean },
    fieldId: string
): FieldDefinitionWithDescription => {
    return {
        id: fieldId,
        reference: {
            id: fieldId,
            type: 'FieldDefinition',
            uriPrefix: 'fieldDefinition',
            label: field.label
        },
        fieldType: field.fieldType,
        fieldName: field.name,
        group: field.group,
        name: field.name,
        label: field.label,
        headline: '',
        uri: '',
        description: field.description || '',
        metadata: field?.metadata,
        isRequired: field?.isRequired ?? false,
        isKey: field?.isKey ?? false
    };
};
